import React, { Fragment } from 'react';
import { times } from 'lodash-es';
import cx from 'classnames';

import ActionsDropdown from '../ActionsDropdown';
import Tooltip from '../../../../../components/Tooltip';
import AppIcon from '../../../../../components/AppIcon';
import OrganizationIcon from '../../../../../components/OrganizationIcon';
import SimpleCheckbox from '../../../../../components/Checkbox/SimpleCheckbox';
import Button from '../../../../../components/Buttons/Button';
import Skeleton from '../../../../../components/Skeleton';
import CsvExportButton from '../../../../../components/Buttons/CsvExportButton';
import ProgressBar from '../../../../../components/HorizontalBars/ProgressBar';

import { DATA_POINTS } from '../../../configs/custom-exports-config';
import { getReportStatus } from '../../../utils/custom-exports-utils';
import { format } from '../../../../../utils/date';
import { assetMapping } from '../../../../../utils/helpers';

const shownEntitiesCount = 3;

const dataPointsStringify = (dataPoints) =>
  dataPoints.map((dataPoint) => DATA_POINTS[dataPoint]).join(', ');

const renderActions = ({
  item,
  pending,
  inProgress,
  queued,
  queuePosition,
  progress,
  bigReportError,
  failed,
  updateReportButton,
  exportReportButton,
  actionsDropdown,
}) => {
  if (queued) {
    return (
      <div className="d-flex align-items-center gap-8">
        <span className="text-black text-14">Queued #{queuePosition}</span>
      </div>
    );
  }

  if (pending) {
    if (item.export_type === 'csv') {
      return inProgress && progress != null ? (
        <ProgressBar
          className="generate-export-progress-bar"
          percentage={Math.round(progress)}
          barWidth="62px"
          textWidth="26px"
          textPlacement="right"
        />
      ) : (
        <Skeleton width={88} height={16} />
      );
    }
    return (
      <div className="d-flex align-items-center gap-8">
        <span className="text-black text-14">In Progress</span>
        {item.export_type === 'xls' && (
          <Tooltip
            id="xlsx-export-in-progress-tooltip"
            content="Percent complete is not available for XLSX exports."
            placement="left"
            colorVariant="light"
          >
            <span className="hicon-question-circle text-quiet text-16" />
          </Tooltip>
        )}
      </div>
    );
  }

  if (bigReportError || failed) {
    const bigReportErrorTooltipText =
      'Error: File size too large. Try reducing the scope of this report to reduce its size (ex fewer countries).';
    const errorTooltipText =
      'This export did not finish successfully. Our team has been notified. You will receive an email when this issue has been resolved and the export is ready.';

    return (
      <>
        <Tooltip
          id="export-failed-tooltip"
          content={bigReportError ? bigReportErrorTooltipText : errorTooltipText}
          placement="left"
          colorVariant="light"
        >
          <span className="exports-actions-failed-export">Failed</span>
        </Tooltip>
        {actionsDropdown}
      </>
    );
  }

  return (
    <>
      {updateReportButton}
      {exportReportButton}
      {actionsDropdown}
    </>
  );
};

// Render Row Cells
export const renderRowCells = (
  item,
  { selectedExportsIds },
  {
    onExportSelect,
    onExportUpdate,
    onExportEdit,
    onExportRename,
    onExportDuplicate,
    onExportDelete,
    onSchedule,
    onScheduleUpdate,
    onScheduleCancel,
    scheduleLimitReached,
  }
) => {
  const { pending, inProgress, queued, queuePosition, progress, failed, bigReportError, disabled } =
    getReportStatus(item);
  const selected = selectedExportsIds.includes(item.id);
  const extraEntitiesCount = item.entities.length - shownEntitiesCount;
  const mapFrequency = {
    weekly: 'week',
    monthly: 'month',
    quarterly: 'quarter',
  };

  const schedulingTooltip = item.frequency
    ? `Scheduled to run every ${mapFrequency[item.frequency]}`
    : null;

  return {
    key: item.id,
    rowClassName: cx(`status-${item.state}`, pending && 'text-muted'),
    checkbox: (
      <div onClick={(event) => event.stopPropagation()}>
        <SimpleCheckbox
          size="lg"
          id={item.id}
          checked={selected}
          disabled={disabled}
          className="single-export-selection"
          withHoverEffect
          onChange={(checked) => onExportSelect(checked, item)}
        />
      </div>
    ),
    name: (
      <div className="d-flex">
        <Tooltip content={item.name || item.id}>
          <span className={cx('text-truncate', { 'text-muted': pending })}>
            {item.name || item.id}
          </span>
        </Tooltip>
      </div>
    ),
    entities: (
      <div className="entities-cell">
        <div className="entities-cell__left">
          {item.first_entities.slice(0, shownEntitiesCount).map((entity) => {
            if (assetMapping.apps.includes(entity.kind)) {
              return (
                <AppIcon
                  key={entity.ref_no}
                  alt={entity.name}
                  url={entity.icon_url}
                  store={entity.store}
                  size={24}
                  withBorder
                  hideHtmlTitle
                />
              );
            }

            return (
              <div key={entity.ref_no} className="organization-block">
                <OrganizationIcon key={entity.ref_no} kind={entity.kind} size={16} />
              </div>
            );
          })}
        </div>
        {extraEntitiesCount > 0 && (
          <div className="entities-cell__right">+{extraEntitiesCount}</div>
        )}
      </div>
    ),
    date_range: (
      <div className="text-right">
        {format(item.start_date, 'MM/DD/YY')} - {format(item.end_date, 'MM/DD/YY')}
      </div>
    ),
    data_points: (
      <div className="text-truncate">
        {item.data_type === 'mpi' ? 'MPI' : dataPointsStringify(item.data_points)}
      </div>
    ),
    created_at: <div className="text-right">{format(item.created_at, 'MM/DD/YY')}</div>,
    last_run: (
      <div className="d-flex align-items-center justify-content-end gap-8">
        {schedulingTooltip && (
          <Tooltip content={schedulingTooltip} colorVariant="light">
            <span className="hicon-calendar-thin-round text-blue" />
          </Tooltip>
        )}
        <p className="text-right">
          {item.last_run_at ? format(item.last_run_at, 'MM/DD/YY') : '-'}
        </p>
      </div>
    ),
    actions: (
      <div
        className="exports-actions-buttons"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {renderActions({
          item,
          pending,
          inProgress,
          queued,
          queuePosition,
          progress,
          bigReportError,
          failed,
          updateReportButton: (
            <Tooltip
              id="update-action-tooltip"
              content="Updates the end-date of the time period to the most recently available date and regenerates the export."
              placement="topRight"
            >
              <Button
                size="sm"
                shape="square"
                variant="secondary"
                onClick={() => onExportUpdate(item)}
              >
                <i className="hicon-reset" />
              </Button>
            </Tooltip>
          ),
          exportReportButton: (
            <CsvExportButton
              url={`/custom_exports/${item.id}.csv`}
              size="sm"
              tooltipText="Download"
            />
          ),
          actionsDropdown: (
            <ActionsDropdown
              size="sm"
              item={item}
              scheduleLimitReached={scheduleLimitReached}
              onEdit={onExportEdit}
              onRename={onExportRename}
              onDuplicate={onExportDuplicate}
              onDelete={onExportDelete}
              onSchedule={failed || bigReportError ? undefined : onSchedule} // Disable scheduling if the report has failed
              onScheduleUpdate={onScheduleUpdate}
              onScheduleCancel={onScheduleCancel}
            />
          ),
        })}
      </div>
    ),
  };
};

// Loading More Rows
const renderLoader = () => <Skeleton width="80%" />;
export const skeletonRows = times(10, (index) => ({
  key: `loading-row-${index}`,
  checkbox: (
    <div>
      <SimpleCheckbox
        size="lg"
        id={`loading-row-${index}`}
        checked={false}
        className="single-pub-selection"
        withHoverEffect
        disabled
      />
    </div>
  ),
  name: renderLoader(),
  entities: renderLoader(),
  date_range: renderLoader(),
  data_points: renderLoader(),
  created_at: renderLoader(),
  last_run: renderLoader(),
  actions: (
    <div className="exports-actions-buttons d-flex">
      <Skeleton height={24} width={24} />
      <Skeleton height={24} width={24} />
      <Skeleton height={24} width={24} />
    </div>
  ),
}));
